import { Alert, Box, Stack, Tab, Tabs } from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import ExperienceRating from "components/pricing/lossRating/ExperienceRating";
import LossRatingInputs from "components/pricing/lossRating/LossRatingInputs";
import LossRatingResults from "components/pricing/lossRating/LossRatingResults";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as programActions from "store/actions/input/program/programActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const ENGINE = "united_es_loss_v1";

const SECTIONS = {
  gl: {
    label: "General Liability",
    resultKey: "gl",
    getData: programSelectors.getLossRating,
    getResult: programSelectors.getLossRatingResult,
    update: programActions.updateLossRating,
    setResult: programActions.setLossRatingResult,
  },
  auto: {
    label: "Auto Liability",
    resultKey: "auto",
    getData: programSelectors.getAutoLossRating,
    getResult: programSelectors.getAutoLossRatingResult,
    update: programActions.updateAutoLossRating,
    setResult: programActions.setAutoLossRatingResult,
  },
};

// gl/auto render the shared loss-rating section; experience renders the
// (GL-fed) ISO experience-rating block.
const TABS = [
  { key: "gl", label: "General Liability" },
  { key: "auto", label: "Auto Liability" },
  { key: "experience", label: "ISO Experience Rating" },
];

const LossRatingSection = ({ variant }) => {
  const section = SECTIONS[variant];
  const dispatch = useDispatch();
  const data = useSelector(section.getData);
  const result = useSelector(section.getResult);
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );

  const [error, setError] = useState(null);

  const [
    _triggerPricing,
    { isFetching },
  ] = analyticsService.useLazySubmissionAnalyticsQuery();

  const handleUpdate = useCallback(
    (field, value) => dispatch(section.update(field, value)),
    [dispatch, section]
  );

  const handleCalculate = async () => {
    setError(null);
    if (data?.projectedExposure == null) {
      setError(
        "Please set Projected Exposure in Rating Parameters before calculating."
      );
      return;
    }
    try {
      const response = await _triggerPricing({
        engine: ENGINE,
        metric: "program_pricing",
        submission,
      });
      if (response?.data?.failure) {
        setError(response.data.failure);
        dispatch(section.setResult(null));
      } else {
        const blockResult = response?.data?.values?.[section.resultKey] ?? null;
        if (!blockResult) {
          setError(
            "No result returned — please check Projected Exposure and policy year data."
          );
          dispatch(section.setResult(null));
        } else {
          dispatch(section.setResult(blockResult));
        }
      }
    } catch (err) {
      setError(err?.message ?? "Pricing request failed.");
      dispatch(section.setResult(null));
    }
  };

  return (
    <Stack direction={"column"} gap={2} sx={{ pr: 2 }}>
      <LossRatingInputs data={data} onUpdate={handleUpdate} />
      <Stack
        direction={"row"}
        justifyContent={"flex-end"}
        alignItems={"center"}
        spacing={2}
      >
        {error && (
          <Alert severity={"error"} sx={{ width: "100%" }}>
            {error}
          </Alert>
        )}
        <Button onClick={handleCalculate} isLoading={isFetching}>
          {"Calculate"}
        </Button>
      </Stack>
      {result && (
        <LossRatingResults
          values={result}
          data={data}
          onUpdate={handleUpdate}
        />
      )}
    </Stack>
  );
};

const LossRatingPricing = () => {
  const [tab, setTab] = useState(0);
  const activeKey = TABS[tab].key;

  return (
    <Disabled ifReadOnly>
      <Component title={"Loss Rating"}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                fontSize: "0.8rem",
                textTransform: "none",
                fontWeight: 500,
                px: 2,
                py: 0.75,
              },
            }}
          >
            {TABS.map(({ key, label }) => (
              <Tab key={key} label={label} disableRipple />
            ))}
          </Tabs>
        </Box>
        {/* All stay mounted so each section's state survives a tab switch. */}
        {TABS.map(({ key }) => (
          <Box key={key} sx={{ display: key === activeKey ? "block" : "none" }}>
            {key === "experience" ? (
              <ExperienceRating />
            ) : (
              <LossRatingSection variant={key} />
            )}
          </Box>
        ))}
      </Component>
    </Disabled>
  );
};

export default LossRatingPricing;
